// Workflow-step help for Case Buddy.
//
// Writing guidance for this catalog:
// - Speak to the investigator, not to the implementation.
// - Explain how the step turns incomplete case material into a clearer analysis
//   and, eventually, a report that is ready to share.
// - Define Case Buddy terms the first time they matter.
// - Keep column names and internal mechanics in tips unless the user truly needs them.
// - Describe raw-to-Main preparation as an “initial working flow” or a
//   “first draft of the flow.”
// - Prefer “Flow Solver” over internal tool numbers.
//
// This is a UI support layer only. Main Data, Raw Data, the address legend,
// case metadata, and reviewed deliverables remain the sources of case truth.

function workflowHelp(options = {}) {
  return {
    tag: options.tag || '',
    title: options.title || 'Workflow step',
    summary: options.summary || '',
    goal: options.goal || '',
    why: options.why || '',
    whatToDo: Array.isArray(options.whatToDo) ? options.whatToDo : [],
    doneWhen: Array.isArray(options.doneWhen) ? options.doneWhen : [],
    tips: Array.isArray(options.tips) ? options.tips : []
  };
}

export const CASE_WORKFLOW_STEP_HELP = Object.freeze({
  CASE_INTAKE: workflowHelp({
    tag: 'CASE_INTAKE',
    title: 'Load the starting transactions',
    summary: 'Add the transaction or transactions where this case begins.',
    goal: 'Give Case Buddy a clear starting point for every flow you need to explain.',
    why: 'These transactions are the first pieces of the case. Once they are in place, Case Buddy can build a timeline, follow what happened next, and turn the work into an organized report.',
    whatToDo: [
      'Load or paste each starting transaction. Add the receiving address when you already know it.',
      'Check that every starting transaction appears as its own entry in Main Data.',
      'When the rows are already present, confirm the step instead of loading them a second time.'
    ],
    doneWhen: [
      'Every transaction that should start a case flow appears in Main Data.',
      'The workflow step shows as complete.'
    ],
    tips: [
      'Start with the transactions tied to the loss, incident, or question you are investigating—not every related transaction you have found.',
      'It is fine if you do not yet know where the funds went. That comes later.'
    ]
  }),

  NETWORK_DISCOVERY: workflowHelp({
    tag: 'NETWORK_DISCOVERY',
    title: 'Identify the transaction networks',
    summary: 'Find the blockchain network for each starting transaction.',
    goal: 'Make sure every starting flow is connected to the correct blockchain.',
    why: 'A transaction hash only makes sense on the network where it occurred. Getting the network right keeps later lookups, dates, links, and report references from pointing to the wrong place.',
    whatToDo: [
      'Run the network lookup for the starting transactions.',
      'Review anything Case Buddy could not identify.',
      'Choose the network manually when you can verify it from a trusted source.'
    ],
    doneWhen: [
      'Every starting transaction has a network name or network id.',
      'No starting transaction is still waiting for a network decision.'
    ],
    tips: [
      'Do not guess the network from the token name alone. The same token can exist on more than one blockchain.'
    ]
  }),

  TRANSACTION_DATES: workflowHelp({
    tag: 'TRANSACTION_DATES',
    title: 'Build the starting timeline',
    summary: 'Add the transaction times and put the starting transactions in order.',
    goal: 'Turn a loose set of starting records into a clear timeline.',
    why: 'The timeline becomes the backbone of the case. It shows what happened first and gives the next step a sensible order for naming the flows.',
    whatToDo: [
      'Look up any missing transaction dates and times.',
      'Review dates that could not be found or that look inconsistent with the source material.',
      'Sort the starting transactions by date before assigning their path labels.'
    ],
    doneWhen: [
      'Every starting transaction has a usable UTC date and time.',
      'The starting transactions appear in chronological order.'
    ],
    tips: [
      'UTC keeps the timeline consistent for investigators and readers in different locations.',
      'If you intentionally keep a different order, make sure the reason is clear before you label the flows.'
    ]
  }),

  MAIN_TRANSACTION_PATH_LABELS: workflowHelp({
    tag: 'MAIN_TRANSACTION_PATH_LABELS',
    title: 'Label the starting flows',
    summary: 'Give each starting transaction a short path label, such as 1, 2, or 3.',
    goal: 'Give every flow a simple name that can be used throughout the case.',
    why: 'Short labels are much easier to follow than full transaction hashes. They become the map used in the notes, visuals, tables, and written report.',
    whatToDo: [
      'Assign number labels after the starting transactions are in the order you want.',
      'Check that each starting transaction has one distinct top-level label.',
      'Adjust the labels manually only when the case needs a different naming scheme.'
    ],
    doneWhen: [
      'Every starting transaction has a top-level path label.',
      'The labels are unique and appear in the intended order.'
    ],
    tips: [
      'Later transactions receive longer labels that show how they relate to the starting flow—for example, 1a or 1a1.'
    ]
  }),

  CASE_CONTEXT_REVIEW: workflowHelp({
    tag: 'CASE_CONTEXT_REVIEW',
    title: 'Check the case details and starting story',
    summary: 'Pause and make sure the case details, source material, and starting transactions all describe the same incident.',
    goal: 'Begin the tracing work with the right case and the right starting story.',
    why: 'A technically correct trace can still answer the wrong question if the case details or starting transactions are wrong. This is the easiest time to catch that problem.',
    whatToDo: [
      'Review the case name, case id, and case type, and update them when needed.',
      'Read the intake material or early narrative that explains why these transactions matter.',
      'Confirm that the starting transactions belong to this case, then record the review.'
    ],
    doneWhen: [
      'The saved case details are correct or intentionally left blank.',
      'You have confirmed that the starting transactions match the case material.'
    ],
    tips: [
      'This is a human review. Case Buddy can store the acknowledgement, but it cannot know whether the source files were actually read.'
    ]
  }),

  RAW_DATA_FETCH: workflowHelp({
    tag: 'RAW_DATA_FETCH',
    title: 'Gather the traced transaction data',
    summary: 'Collect the transactions that show where each starting transfer went next.',
    goal: 'Create a preserved tracing record for every starting flow.',
    why: 'Raw Data is the source material behind the working case. Keeping it separate means you can build, correct, or extend the case without losing the original tracing results.',
    whatToDo: [
      'Use the Flow Solver to follow a starting transaction, or import results from another tracing source.',
      'Continue each relevant branch until it reaches a meaningful ending or a decision that needs your review.',
      'Save or paste the results into Raw Data, then repeat for the remaining starting flows.'
    ],
    doneWhen: [
      'Each starting flow has a matching set of Raw Data.',
      'The important branches have understandable endings, or the remaining uncertainty is clearly identified for review.'
    ],
    tips: [
      'Raw Data is the preserved tracing record. Saving it does not automatically change Main Data.',
      'A complete trace does not mean copying every transaction on the blockchain. It means following the funds that matter to this case.'
    ]
  }),

  RAW_TO_MAIN_PREPARATION: workflowHelp({
    tag: 'RAW_TO_MAIN_PREPARATION',
    title: 'Build the first working flows',
    summary: 'Turn the preserved tracing results into a clean first draft of each flow in Main Data.',
    goal: 'Move from collected tracing material to a readable, editable case graph.',
    why: 'Raw Data can contain repeated, noisy, or source-oriented rows. This step organizes those rows into coherent paths, labels, and followed amounts while keeping the original Raw Data intact.',
    whatToDo: [
      'Open the preparation tool and choose a starting flow backed by Raw Data.',
      'Review the proposed path structure, branches, and followed amounts.',
      'Apply the prepared rows to Main Data, then repeat for the other starting flows.'
    ],
    doneWhen: [
      'Every traced starting transaction has a coherent working flow in Main Data.',
      'The workflow confirms that the current Raw Data has been prepared.'
    ],
    tips: [
      'This is the first working draft of the flow, not the final analysis. You can still prune paths and graft new tracing onto it later.',
      'Preparing Main Data does not erase or replace the source rows in Raw Data.'
    ]
  }),

  ADDRESS_ATTRIBUTION_FILL: workflowHelp({
    tag: 'ADDRESS_ATTRIBUTION_FILL',
    title: 'Identify the addresses',
    summary: 'Add the best supported name or type for the addresses in the working case.',
    goal: 'Replace anonymous endpoints with known services, known entities, useful address types, or an honest Unknown.',
    why: 'Address identification turns a chain of hashes into an understandable story. It also helps you decide where a path should stop, where more tracing is needed, and how the final report should describe the destination.',
    whatToDo: [
      'Review the addresses Case Buddy has gathered from Main Data.',
      'Accept or enter a name when the evidence supports it.',
      'Use Unknown when the address cannot be identified with reasonable confidence.'
    ],
    doneWhen: [
      'Every address that requires a decision has an attribution or Unknown.',
      'The attribution check no longer shows missing required values.'
    ],
    tips: [
      'Unknown is a valid finding. It is better than an unsupported name.',
      'An address attribution describes what the address appears to be; it does not automatically prove who controlled it.'
    ]
  }),

  ATTRIBUTION_PATH_OPERATIONS: workflowHelp({
    tag: 'ATTRIBUTION_PATH_OPERATIONS',
    title: 'Decide where each path goes next',
    summary: 'Use the address findings to stop, remove, or extend each path.',
    goal: 'Shape the first draft into an accurate story of the funds you are following.',
    why: 'Not every visible transfer belongs in the report. Some paths reach a meaningful ending, some need more tracing, and some move to another blockchain through a bridge.',
    whatToDo: [
      'Review the path candidates one at a time and read the surrounding transaction details.',
      'Prune a path when it should no longer continue in the case.',
      'Graft more traced rows when the same path continues, or use a Bridge graft when the funds move to another network.',
      'Review the proposed change before applying it.'
    ],
    doneWhen: [
      'The current prune, graft, and Bridge decisions have been handled or intentionally left for later.',
      'The workflow records that the path-operation review is complete.'
    ],
    tips: [
      'Pruning the working case does not erase the preserved Raw Data.',
      'A Bridge graft may need extra review of timing, token, amount, and the destination-network transaction.'
    ]
  }),

  ADDRESS_COLOR_LIKE_CELLS: workflowHelp({
    tag: 'ADDRESS_COLOR_LIKE_CELLS',
    title: 'Highlight repeated addresses',
    summary: 'Give matching From and To addresses the same color in the spreadsheet.',
    goal: 'Make address reuse and reconnecting paths easier to notice at a glance.',
    why: 'Long or branching cases can be hard to read as rows alone. Matching colors provide a quick visual clue when the same address appears again elsewhere in the case.',
    whatToDo: [
      'Apply the address colors after the main flow structure is in place.',
      'Scan the spreadsheet to make sure repeated addresses are easy to recognize.',
      'Clear and reapply the colors after major case changes when needed.'
    ],
    doneWhen: [
      'Matching addresses use matching colors, or you have intentionally chosen not to keep the coloring.'
    ],
    tips: [
      'This changes spreadsheet styling only. It does not change the transaction data or the address attribution.'
    ]
  }),

  ENTITY_REVIEW: workflowHelp({
    tag: 'ENTITY_REVIEW',
    title: 'Review newly found entity names',
    summary: 'Decide which names Case Buddy should remember and which text fragments it should ignore.',
    goal: 'Keep entity recognition useful, consistent, and free of noise.',
    why: 'A clean recognition list improves later suggestions and prevents random note fragments from being mistaken for exchanges, services, or other real entities.',
    whatToDo: [
      'Review each new or differently written entity name.',
      'Match known names to the preferred spelling and capitalization.',
      'Remember real entities that will be useful again, and dismiss or suppress text that is not an entity.',
      'Finish the review when the current list has been handled.'
    ],
    doneWhen: [
      'The current entity-name candidates have been accepted, corrected, dismissed, or intentionally left unchanged.',
      'The entity review is marked complete.'
    ],
    tips: [
      'Adding a name to the recognition list does not prove the attribution. Only add names you are comfortable seeing suggested again.',
      'When the only difference is capitalization, correct the spelling instead of creating a second version of the same entity.'
    ]
  }),

  EXTERNAL_ATTRIBUTION_REVIEW: workflowHelp({
    tag: 'EXTERNAL_ATTRIBUTION_REVIEW',
    title: 'Complete any outside address research',
    summary: 'Record the address research that took place outside Case Buddy.',
    goal: 'Give important unknown or uncertain addresses an appropriate second look before the case is finalized.',
    why: 'Useful findings may come from explorers, commercial tools, open-source research, or case records that Case Buddy cannot see. This step makes sure that work is reflected in the case before reporting begins.',
    whatToDo: [
      'Identify the addresses that are important enough to justify outside research.',
      'Use your normal research sources and keep track of the support for any new finding.',
      'Update the address attribution or notes when the research changes the case.',
      'Mark the review complete when no further outside checks are needed for this version of the case.'
    ],
    doneWhen: [
      'The necessary outside research is complete, or you have decided that it is not needed.',
      'Any useful findings have been added to the case.'
    ],
    tips: [
      'Not every Unknown address needs outside research. Focus on addresses that could change the case outcome or the way a path ending is described.'
    ]
  }),

  CASE_INCONSISTENCIES: workflowHelp({
    tag: 'CASE_INCONSISTENCIES',
    title: 'Resolve conflicting case details',
    summary: 'Review places where related rows do not agree or where an important detail is missing.',
    goal: 'Correct contradictions before they reach the visuals, tables, or written report.',
    why: 'A single wrong continuation, label, amount, or ending can make a polished deliverable tell the wrong story. This review catches those problems while they are still easy to trace back to the spreadsheet.',
    whatToDo: [
      'Run the inconsistency review and read each finding in context.',
      'Apply safe fixes when the proposed change clearly matches the case data.',
      'Handle the remaining findings one at a time when they require judgement.',
      'Run the check again after making changes.'
    ],
    doneWhen: [
      'No required inconsistency remains unresolved.',
      'Any warning you chose to keep is understood and intentional.'
    ],
    tips: [
      'A warning can sometimes describe a legitimate complex flow. Do not force a fix simply to make the warning disappear.'
    ]
  }),

  NORMALIZE_DEPOSIT_NOTES: workflowHelp({
    tag: 'NORMALIZE_DEPOSIT_NOTES',
    title: 'Clean up service and deposit notes',
    summary: 'Turn rough notes about deposits, hot wallets, and unknown services into clear, consistent language.',
    goal: 'Make path endings understandable and ready to reuse in the report.',
    why: 'The notes in Main Data become labels, summaries, and report sentences. Consistent wording helps the same kind of destination read the same way throughout the case.',
    whatToDo: [
      'Review the suggested before-and-after wording for each note.',
      'Apply the cleanup when it preserves the meaning of the evidence.',
      'Research or leave the note unchanged when the suggestion is too uncertain.',
      'Finish the review when the safe cleanup list has been handled.'
    ],
    doneWhen: [
      'No safe note cleanup remains pending, or the remaining notes were intentionally left unchanged.'
    ],
    tips: [
      'This step improves wording; it is not a substitute for address research or a new attribution decision.'
    ]
  }),

  HIGH_PRIORITY_DEPOSIT_PATTERNS: workflowHelp({
    tag: 'HIGH_PRIORITY_DEPOSIT_PATTERNS',
    title: 'Review likely deposit endings',
    summary: 'Review transactions that look like the funds may have reached an exchange, service, or other meaningful endpoint.',
    goal: 'Catch important path endings that may have been missed during the first tracing pass.',
    why: 'Timing, amount, and movement patterns can reveal a likely deposit even when the address label or note is incomplete. Reviewing these candidates helps prevent a path from stopping too early or continuing past a meaningful destination.',
    whatToDo: [
      'Read the evidence shown for each candidate.',
      'Accept the suggested ending when the pattern and surrounding data support it.',
      'Dismiss candidates that do not fit, and research the uncertain ones when they matter.',
      'Complete the review after every high-priority candidate has a decision.'
    ],
    doneWhen: [
      'No high-priority deposit-pattern candidate is still waiting for review.'
    ],
    tips: [
      'A pattern is a lead, not proof. Use the transaction context and other available evidence before changing the case.'
    ]
  }),

  KNOWN_ENTITY_NOTE_CASING: workflowHelp({
    tag: 'KNOWN_ENTITY_NOTE_CASING',
    title: 'Standardize entity names',
    summary: 'Make known entity names use one consistent spelling and capitalization.',
    goal: 'Prevent small writing differences from making one entity look like several different entities.',
    why: 'Consistent names make the case easier to read and help Case Buddy group the same service correctly in tables, summaries, and the report.',
    whatToDo: [
      'Review the safe spelling and capitalization fixes Case Buddy found.',
      'Apply the fixes that clearly refer to the same known entity.',
      'Leave an item unchanged when the proposed name is not right for that row.'
    ],
    doneWhen: [
      'No safe entity-name correction remains pending, or the remaining items were intentionally left alone.'
    ],
    tips: [
      'This step changes presentation and consistency. It does not create a new attribution.'
    ]
  }),

  DUPLICATE_HASH_LABELS: workflowHelp({
    tag: 'DUPLICATE_HASH_LABELS',
    title: 'Explain repeated transaction hashes',
    summary: 'Add a clear note when the same blockchain transaction appears under more than one path label.',
    goal: 'Make intentional transaction reuse easy for a reader to understand.',
    why: 'Funds from different starting flows can converge on the same later transaction. Without an explanation, the report can look duplicated, inconsistent, or double-counted even when the analysis is correct.',
    whatToDo: [
      'Review the transaction hashes that appear under more than one path label.',
      'Apply the explanatory note when each appearance is a valid part of its own flow.',
      'Read any safety warning before changing or completing the step.'
    ],
    doneWhen: [
      'Every eligible repeated transaction has a current explanatory note.',
      'No duplicate-hash safety issue remains unresolved.'
    ],
    tips: [
      'Path labels describe where the same transaction sits in each flow. Do not remove a valid copy simply because the hash appears elsewhere.'
    ]
  }),

  NOTES_BACKUP_AND_HASH_REPLACEMENT: workflowHelp({
    tag: 'NOTES_BACKUP_AND_HASH_REPLACEMENT',
    title: 'Back up and simplify the notes',
    summary: 'Save the original notes, then replace long transaction hashes with readable path labels where possible.',
    goal: 'Make the case notes easier for a person to follow without losing the original wording.',
    why: 'A note that says “continues as 1b” is easier to understand than one built from several full hashes. The backup preserves the original version in case it is needed later.',
    whatToDo: [
      'Create or refresh the backup of the original notes.',
      'Review the proposed hash-to-path-label replacements.',
      'Apply replacements that are clear and unambiguous.',
      'Keep the backup with the case for later reference.'
    ],
    doneWhen: [
      'A current backup of the original notes exists.',
      'No safe, unambiguous note replacement remains pending.'
    ],
    tips: [
      'Only hashes that can be matched confidently to a path label should be replaced.',
      'The transaction hashes remain in the spreadsheet; this step only makes the note text easier to read.'
    ]
  }),

  WWW_USD_REFERENCES: workflowHelp({
    tag: 'WWW_USD_REFERENCES',
    title: 'Set the starting USD values',
    summary: 'Add a USD reference value for each starting transaction.',
    goal: 'Give the later destination summary a clear and consistent value basis.',
    why: 'A case may contain different tokens or transactions from different dates. Recording the starting USD values now helps the final totals make sense and avoids changing the underlying case after the report sections are already built.',
    whatToDo: [
      'Review the suggested value for each starting transaction.',
      'Check that the date, token, and amount used for the estimate are correct.',
      'Enter a better supported value when you have one, then apply the result.',
      'Leave a value blank only when that is an intentional and understood decision.'
    ],
    doneWhen: [
      'Every starting transaction has the USD reference it needs, or was intentionally left without one.'
    ],
    tips: [
      'The USD reference supports reporting. It does not replace the original token amount.',
      'When a value is estimated, keep the wording and supporting record clear that it is an estimate.'
    ]
  }),

  ADDRESS_LEGEND: workflowHelp({
    tag: 'ADDRESS_LEGEND',
    title: 'Build the address legend',
    summary: 'Give important addresses friendly, consistent names for this case.',
    goal: 'Replace repeated raw addresses with labels a reader can recognize across the report and visuals.',
    why: 'Labels such as “Victim’s Address #1” or “Suspect’s Binance Address #1” make a complex case much easier to follow and keep the same address named consistently everywhere.',
    whatToDo: [
      'Build the suggested legend from the current case data.',
      'Review each new or changed label, including its role and entity name.',
      'Edit labels that would be unclear or misleading to a reader.',
      'Mark the legend reviewed when the current list is ready to use.'
    ],
    doneWhen: [
      'The important case addresses have clear, consistent labels.',
      'The address legend is reviewed and current for the case.'
    ],
    tips: [
      'The legend is specific to this case. A friendly label describes how the address is used in the case; it does not by itself prove legal ownership.'
    ]
  }),

  TEMPLATE_DELIVERABLE: workflowHelp({
    tag: 'TEMPLATE_DELIVERABLE',
    title: 'Build the case template',
    summary: 'Create the clean, structured case table used in the report package.',
    goal: 'Turn the working spreadsheet into a standardized output that is easier to review, share, and reuse.',
    why: 'Main Data contains the working detail of the investigation. The template presents the important information in a consistent form for readers who do not need to work directly in the case spreadsheet.',
    whatToDo: [
      'Build the template from the current Main Data and address legend.',
      'Review the rows for missing labels, unclear names, or information that does not match the case.',
      'Correct the underlying case data when something is wrong, then rebuild the template.',
      'Mark the template reviewed when it is ready to export or include in the package.'
    ],
    doneWhen: [
      'The template matches the current case data and has been reviewed.'
    ],
    tips: [
      'Whenever practical, fix a problem in Main Data or the address legend instead of hand-editing only the generated output.'
    ]
  }),

  SUSPICIOUS_ADDRESS_TABLE: workflowHelp({
    tag: 'SUSPICIOUS_ADDRESS_TABLE',
    title: 'Build the suspicious address table',
    summary: 'Create a focused list of addresses the case treats as suspicious or important to the investigation.',
    goal: 'Give the reader a quick reference to the addresses that may require attention or follow-up.',
    why: 'The detailed flow can contain many addresses. This table pulls the relevant ones into one place so they do not get lost inside the transaction-by-transaction narrative.',
    whatToDo: [
      'Build the table from the current case and address legend.',
      'Review which addresses were included and how they are labelled.',
      'Remove items that do not belong in this particular table without deleting valid case data.',
      'Mark the table reviewed when it accurately reflects the case.'
    ],
    doneWhen: [
      'The suspicious address table is current, understandable, and reviewed.'
    ],
    tips: [
      'The table summarizes the case analysis. Its inclusion is not, by itself, proof that an address belongs to a particular person.'
    ]
  }),

  EXCHANGE_ADDRESS_TABLE: workflowHelp({
    tag: 'EXCHANGE_ADDRESS_TABLE',
    title: 'Build the exchange and service address table',
    summary: 'Create a list of exchange or service addresses reached by the followed funds.',
    goal: 'Collect the service endpoints and their related transaction details in one clear place.',
    why: 'These destinations are often the practical handoff for provider outreach, additional records requests, or later follow-up. The table keeps that information separate from the longer case narrative.',
    whatToDo: [
      'Build the table from the current path endings and address findings.',
      'Confirm that each included address is actually associated with the named exchange or service.',
      'Review the labels, transaction details, and any excluded rows.',
      'Mark the table reviewed when it is ready to use.'
    ],
    doneWhen: [
      'The current exchange and service endpoints are represented accurately in the reviewed table.'
    ],
    tips: [
      'When the entity name is wrong or uncertain, correct the attribution in the case first and rebuild the table.'
    ]
  }),

  DORMANT_ADDRESS_DELIVERABLES: workflowHelp({
    tag: 'DORMANT_ADDRESS_DELIVERABLES',
    title: 'Build the dormant-funds outputs',
    summary: 'Create the table and monitoring view for funds that appear to remain at an address.',
    goal: 'Separate apparently unmoved funds from exchange, service, and other path endings, and make them easy to monitor later.',
    why: 'Funds that have not moved may become important after the report is written. A clear table and monitoring view let you follow those addresses without rebuilding the case from scratch.',
    whatToDo: [
      'Build the dormant-address table and monitoring view.',
      'Review the address, amount, token, and reason each item is treated as dormant.',
      'Remove or correct entries that have a meaningful onward movement in the case data.',
      'Mark the outputs reviewed when they are ready.'
    ],
    doneWhen: [
      'The dormant-funds outputs match the current case, even when the correct result is that no dormant funds were found.'
    ],
    tips: [
      'Dormant means no relevant onward movement was identified in the data reviewed for this case. It does not guarantee that the funds will remain there.'
    ]
  }),

  WHAT_WENT_WHERE: workflowHelp({
    tag: 'WHAT_WENT_WHERE',
    title: 'Summarize where the funds went',
    summary: 'Turn the path endings into a clear destination breakdown.',
    goal: 'Answer, at a glance, where the followed funds ended up and how much reached each type of destination.',
    why: 'This summary is the bridge between the detailed tracing work and the final conclusion. It brings exchanges, services, dormant addresses, unknown endings, amounts, and values into one understandable view.',
    whatToDo: [
      'Build the destination summary from the reviewed path endings.',
      'Check every destination group, amount, token, and USD value.',
      'Follow any unexpected result back to Main Data, the address attribution, or the USD references and correct it there.',
      'Review the valuation audit, then mark the summary complete.'
    ],
    doneWhen: [
      'The destination summary and its valuation record match the current case and have been reviewed.'
    ],
    tips: [
      'The totals should make sense against the funds followed, with any known limits or estimates explained.',
      'Do not edit the summary merely to hide an upstream data problem; correct the case and rebuild it.'
    ]
  }),

  CASE_CONCLUSION: workflowHelp({
    tag: 'CASE_CONCLUSION',
    title: 'Write the case conclusion',
    summary: 'Create a short, plain-language answer to what the tracing found.',
    goal: 'Give the reader the high-level result without requiring them to inspect every transaction.',
    why: 'The conclusion is often the first or only part of the analysis that a reader studies closely. It must match the reviewed destination totals and express the level of certainty supported by the case.',
    whatToDo: [
      'Generate the conclusion from the current destination summary.',
      'Read it carefully for the correct entities, amounts, totals, and limitations.',
      'Edit wording that requires investigator judgement or a more accurate level of certainty.',
      'Mark the conclusion reviewed when it says what the evidence actually supports.'
    ],
    doneWhen: [
      'The conclusion is current, accurate, and reviewed as a report paragraph.'
    ],
    tips: [
      'Avoid stronger language than the evidence supports. A clear limitation is better than an overconfident conclusion.'
    ]
  }),

  REPORT_BODY: workflowHelp({
    tag: 'REPORT_BODY',
    title: 'Build the report narrative',
    summary: 'Create the detailed, transaction-by-transaction story of the case.',
    goal: 'Turn the path labels, dates, amounts, addresses, and notes into a narrative another person can follow.',
    why: 'This is where the spreadsheet analysis becomes a written explanation. It should guide the reader through the movement of funds, including branches, converging paths, and meaningful endings.',
    whatToDo: [
      'Build the report body from the current case data and address legend.',
      'Read it in order as though you were seeing the case for the first time.',
      'Check the transitions between transactions, branches, path endings, amounts, and entity names.',
      'Correct source data when needed, rebuild the narrative, and mark it reviewed.'
    ],
    doneWhen: [
      'The report body matches the current case and has been read and approved as a complete narrative.'
    ],
    tips: [
      'Generated text is a draft, even when the data is correct. The investigator should still review clarity, emphasis, and certainty.',
      'The final one-click report uses this reviewed narrative.'
    ]
  }),

  PATH_ENDING_ADDENDUM_REVIEW: workflowHelp({
    tag: 'PATH_ENDING_ADDENDUM_REVIEW',
    title: 'Choose the path endings for the addendum',
    summary: 'Select the exchange or service endings that belong in the supporting addendum.',
    goal: 'Create an accurate list of relevant provider-facing endpoints without adding unrelated rows.',
    why: 'The addendum may be read or used separately from the main narrative. It should contain the true path endings that matter for follow-up, not every address that happened to appear in the case.',
    whatToDo: [
      'Review the rows Case Buddy proposes for the addendum.',
      'Confirm that each included row is a true path ending and is relevant to the addendum.',
      'Review why other rows were omitted and change the decision when the case supports it.',
      'Complete the review even when the correct result is that there are no addendum rows.'
    ],
    doneWhen: [
      'The addendum candidate list is reviewed and current, including a valid no-addendum result.'
    ],
    tips: [
      'Victim-owned addresses, intermediate transfers, and non-ending rows may be correctly omitted.'
    ]
  }),

  ADDENDUM_RECIPIENT_ENTITY: workflowHelp({
    tag: 'ADDENDUM_RECIPIENT_ENTITY',
    title: 'Confirm the recipient for each addendum row',
    summary: 'Choose the exchange or service that actually received each selected path ending.',
    goal: 'Attach every addendum entry to the correct recipient entity.',
    why: 'A transaction note can mention more than one service or address role. Choosing the wrong recipient could make the addendum confusing or direct follow-up to the wrong organization.',
    whatToDo: [
      'Confirm rows where one recipient is clear.',
      'For rows with more than one possible entity, review the receiving address, transaction, and notes.',
      'Choose the entity associated with the address that received the followed funds.',
      'Mark the recipient review complete when every current row is resolved.'
    ],
    doneWhen: [
      'Every addendum row has one supported recipient entity, or there are no rows to resolve.'
    ],
    tips: [
      'Choose the entity that received the funds, not simply an entity name mentioned elsewhere in the transaction story.'
    ]
  }),

  ADDENDUM_BUILD: workflowHelp({
    tag: 'ADDENDUM_BUILD',
    title: 'Build the addendum',
    summary: 'Turn the selected and resolved path endings into the finished supporting section.',
    goal: 'Create a concise, document-ready addendum and its review record.',
    why: 'The addendum packages important exchange or service endpoints in a consistent form that can be used outside the full transaction narrative.',
    whatToDo: [
      'Build the addendum from the reviewed path-ending list and recipient decisions.',
      'Read the formatted and narrative versions for correct entities, amounts, transaction details, and path labels.',
      'Copy, stage, or download the version you need.',
      'Mark the addendum reviewed, or confirm that no addendum is needed.'
    ],
    doneWhen: [
      'The addendum is current and reviewed, or the reviewed result is that the case does not need one.'
    ],
    tips: [
      'A no-addendum result is a valid outcome when no qualifying path ending exists.'
    ]
  }),

  CASE_FLOW_VISUALS: workflowHelp({
    tag: 'CASE_FLOW_VISUALS',
    title: 'Create the case flow visuals',
    summary: 'Create diagrams that show how each starting transaction moved and branched.',
    goal: 'Give the reader an at-a-glance map of the case.',
    why: 'A visual can make a complicated flow understandable much faster than a spreadsheet. It also gives you one more chance to notice a label, branch, merge, bridge, or ending that does not match the written story.',
    whatToDo: [
      'Preview every starting flow, not only the first image.',
      'Check the path labels, branches, merged transactions, Bridge movements, address names, and endings.',
      'Correct Main Data or the address legend when the visual reveals a problem, then rebuild it.',
      'Download the visual package when needed and mark the visuals reviewed.'
    ],
    doneWhen: [
      'Every case flow visual matches the current case and has been reviewed.'
    ],
    tips: [
      'The visuals are generated from Main Data and the address legend. Fix the underlying case information rather than editing only the image.'
    ]
  }),

  CASE_COMPLETION_SUMMARY: workflowHelp({
    tag: 'CASE_COMPLETION_SUMMARY',
    title: 'Review and export the final report',
    summary: 'Assemble all reviewed case sections into one final package.',
    goal: 'Produce a report that is ready to read, copy, download, and share.',
    why: 'This is the final quality-control pass. It brings together the narrative, tables, conclusion, addendum, visuals, and supporting sections so you can confirm that the complete package tells one consistent story.',
    whatToDo: [
      'Open the one-click report package and read it from beginning to end.',
      'Check that no required section is missing, stale, duplicated, or inconsistent with the case.',
      'Return to the earlier workflow step when something needs to be corrected, then rebuild the package.',
      'Copy or download the final version and mark it reviewed when it is ready to ship.'
    ],
    doneWhen: [
      'The full report package is current, complete, and reviewed.',
      'The workflow shows that the case is complete.'
    ],
    tips: [
      'Opening the package is not the same as reviewing it. Treat this as the last read-through before the report leaves Case Buddy.',
      'When the package reports a stale section, rebuild that section rather than ignoring the warning.'
    ]
  })
});

export function getCaseWorkflowStepHelp(stepTag) {
  const key = String(stepTag || '').trim();
  return CASE_WORKFLOW_STEP_HELP[key] || null;
}
